import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Paperclip } from "lucide-react";
import { observer } from "mobx-react-lite";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useChatStore } from "@/hooks/useChatStore";
import { useDocumentUpload } from "@/hooks/useDocumentUpload";
import { useToast } from "@/hooks/use-toast";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { logger } from "@/utils/logger";
import {
  CHATBOT_SUPPORTED_FILE_EXTENSIONS,
  isBlockedFileType,
  isChatbotSupportedFiletype,
  isIgnoredFile,
} from "@/domains/documents/constants/fileTypes";
import { CHATBOT_FILES_PANEL_QUERY_KEY } from "./ChatbotFilesPanel";

const HIDDEN_PROJECT_QUERY_KEY = "chatbot-attachments-project";

/**
 * How long one file may sit in flight before its chip is called failed. An
 * upload that never settles (a request that neither resolves nor rejects)
 * would leave a chip UPLOADING forever, and every later message in the thread
 * is parked behind such a chip with no way out. Generous enough that a real
 * upload on a slow connection finishes first — and when one does not, the
 * bound cancels it, so the failure the user is shown is real and a resend
 * cannot duplicate the file. The one exception is an upload already creating
 * its document row when the bound passes; `attachUploadedDocument` says so
 * when that one lands.
 */
const UPLOAD_STALL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Resolve when the upload settles or the stall bound passes, whichever is
 * first. Never rejects: an upload that throws is a failed upload, and the
 * caller must still get to fail its chip.
 */
function withStallTimeout(upload: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const stalled = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  const settled = upload.then(
    () => undefined,
    (error: unknown) => {
      void logger.error(
        "ui",
        "chatbot-upload-threw",
        "Chatbot upload rejected",
        error instanceof Error ? error : new Error(String(error))
      );
    }
  );
  return Promise.race([settled, stalled]).finally(() => {
    clearTimeout(timer);
  });
}

export interface ChatbotUploadButtonHandle {
  /** Open the OS file picker. */
  openFilePicker: () => void;
  /** Upload one or more files programmatically (used by drag-and-drop). */
  uploadFiles: (files: FileList | File[]) => Promise<void>;
  /** True while at least one upload is in flight. */
  isUploading: boolean;
}

interface ChatbotUploadButtonProps {
  buildingId: string;
  /** Render the trigger button. When omitted, the component renders only the
   *  hidden <input> — useful when the parent owns the trigger UI (e.g. the
   *  Plus popover) and only needs the imperative `openFilePicker()` handle. */
  renderTrigger?: boolean;
}

/**
 * WHA-3330: direct upload from the chatbot input. Files land in the
 * per-building hidden `franklin-chatbot-attachments` project under the
 * "Uploaded" folder, with `created_by_chatbot = false`. The pipeline picks
 * them up via Realtime and the chatbot can read them once `state >= EXTRACTED`.
 *
 * Mirrors the pattern in `IssueDocumentUploadButton` (per-issue uploads).
 */
export const ChatbotUploadButton = observer(
  forwardRef<ChatbotUploadButtonHandle, ChatbotUploadButtonProps>(function ChatbotUploadButton(
    { buildingId, renderTrigger = false },
    ref
  ) {
    const { toast } = useToast();
    const { userProfile } = useAuth();
    const { currentOrganization, isReady: orgReady } = useOrganizationContext();
    const queryClient = useQueryClient();
    const chatStore = useChatStore();
    const inputRef = useRef<HTMLInputElement>(null);

    const orgId = currentOrganization?.id;
    const userId = userProfile?.id;

    // Resolve the hidden project + "Uploaded" folder for this building.
    // Cached per (org, building, user) — RPC is idempotent so this is safe
    // to call eagerly. `isReady` from useOrganizationContext gates the query
    // during org switches so we never fire with a stale orgId (WHA-3360).
    const { data: hiddenProject } = useQuery({
      queryKey: [HIDDEN_PROJECT_QUERY_KEY, orgId, buildingId, userId],
      enabled: Boolean(orgReady && orgId && buildingId && userId),
      staleTime: 60 * 60 * 1000,
      queryFn: async () => {
        // `enabled` above already gates on a truthy orgId/userId; this guard
        // re-establishes that for the type checker without a non-null assertion.
        if (!orgId || !userId) throw new Error("missing org or user context");
        const { data, error } = await supabase.rpc("ensure_chatbot_attachments_project", {
          p_org_id: orgId,
          p_building_id: parseInt(buildingId, 10),
          p_user_id: Number(userId),
        });
        if (error) throw error;
        const row = Array.isArray(data) ? data[0] : data;
        if (!row) throw new Error("ensure_chatbot_attachments_project returned no row");
        return row;
      },
    });

    const projectIdString = hiddenProject?.project_id
      ? String(hiddenProject.project_id)
      : undefined;

    // Chips whose upload has neither landed nor been given up on, each with the
    // controller that cancels its upload. Only these may still be failed; a
    // chip leaves the map when its upload resolves it or when it is failed,
    // whichever comes first — and failing one always cancels its upload, so a
    // chip is never called failed while its file goes on to land.
    const pendingChipsRef = useRef(new Map<string, AbortController>());
    const uploadChainRef = useRef<Promise<void>>(Promise.resolve());
    const unmountedRef = useRef(false);

    // Unmounting mid-upload cancels the uploads it started. A chip it left
    // UPLOADING in the (long-lived) chat store would park every later send in
    // the thread, and one resolved after the unmount could land in whichever
    // chat the user opened next (see `attachUploadedDocument`).
    // Reset on mount too — StrictMode runs this cleanup once in development.
    useEffect(() => {
      unmountedRef.current = false;
      const pendingChips = pendingChipsRef.current;
      return () => {
        unmountedRef.current = true;
        for (const [placeholderId, controller] of pendingChips) {
          controller.abort();
          chatStore.failUploadingAttachment(placeholderId);
        }
        pendingChips.clear();
      };
    }, [chatStore]);

    // The placeholder chip becomes the real document — in the composer, or on
    // a message the user already sent with it (WHA-3356: the chip then follows
    // documents.state via Realtime until extraction completes). Bound to one
    // upload's own chip, so an upload that lands after its stall bound resolves
    // that chip wherever it still is, and never a later file's.
    const attachUploadedDocument =
      (placeholderId: string, filename: string) => (documentId: number) => {
        // Only a chip still in flight may be attached to. Giving up on one
        // cancels its upload, but not a document row already being created, so
        // one can still land after its stall bound passed or the panel unmounted.
        // That document does NOT re-enter the composer,
        // because by then the composer is no longer the one that was waiting for
        // it: the stall path has already refused that message with "a file
        // couldn't be uploaded", so a chip appearing next to that error says the
        // opposite; and `attachedDocuments` is one global list shared by every
        // building's chat, so after an unmount the file would attach itself to
        // whichever chat the user opened next. The document is not lost — it is
        // in the hidden project, listed in the files panel refreshed below, one
        // click from being attached deliberately. Never a delete either:
        // `onExistingDocument` reports a document that was already in the
        // project, which is not ours to remove.
        if (pendingChipsRef.current.delete(placeholderId)) {
          chatStore.resolveUploadingAttachment(placeholderId, String(documentId));
        } else if (!unmountedRef.current) {
          // The chip was given up on while its document row was being created,
          // and the row landed. The user has already been told the file couldn't
          // be uploaded, which is now false, so say where it ended up; they attach
          // it from the panel if they still want it. (Silent after an unmount:
          // there is no panel of ours on screen to point at.)
          toast({
            title: "Upload finished late",
            description: `${filename} took too long to wait for, but it uploaded. It is in the chat's files panel.`,
          });
        }
        // Refresh the files panel immediately so the new doc appears
        // without waiting for Realtime or a panel reopen.
        void queryClient.invalidateQueries({
          queryKey: [CHATBOT_FILES_PANEL_QUERY_KEY],
        });
      };

    const { handleFileUpload, isUploading } = useDocumentUpload({
      buildingId,
      projectId: projectIdString,
      requireProject: true,
      analyticsSource: "chat",
      storagePathSegments: ["chatbot", String(userId ?? "anon")],
    });

    /** The gates every entry point applies, silent for OS junk and loud otherwise. */
    const isUploadable = (file: File): boolean => {
      if (isIgnoredFile(file)) return false;

      if (isBlockedFileType(file)) {
        toast({
          variant: "destructive",
          title: "Blocked file type",
          description: `${file.name} is a blocked file type (executable/script).`,
        });
        return false;
      }

      if (!isChatbotSupportedFiletype(file.name)) {
        toast({
          variant: "destructive",
          title: "This filetype is not supported yet.",
          description: `${file.name} — supported types: ${CHATBOT_SUPPORTED_FILE_EXTENSIONS.map((e) => `.${e}`).join(", ")}.`,
        });
        return false;
      }

      return true;
    };

    /** The picker holds its last selection, so the same file re-picked raises no change event. */
    const clearPickerSelection = () => {
      if (inputRef.current) inputRef.current.value = "";
    };

    const uploadFilesSerially = async (list: File[]) => {
      if (list.length === 0) return;
      // Checked before anything this run says or stages: a run queued behind
      // another only starts once its turn comes, which can be after the panel
      // has gone — and nothing here would have a chip to resolve or a panel to
      // toast from. (The loop re-checks it per file, for an unmount mid-run.)
      if (unmountedRef.current) return;

      if (!hiddenProject) {
        toast({
          variant: "destructive",
          title: "Chatbot upload not ready",
          description: "Still preparing the chatbot attachments project. Try again in a moment.",
        });
        return;
      }

      for (const file of list) {
        // The panel went away under us; a chip added now would have nobody to
        // resolve it. The one already in flight was failed by the cleanup.
        if (unmountedRef.current) break;
        if (!isUploadable(file)) continue;

        // Place the file under the "Uploaded" folder (created by the RPC).
        // The relativePath form `<folder>/<filename>` tells useDocumentUpload
        // to drop into that folder via get_or_create_folder.
        // The chip shows the moment the file is picked, so a message sent while
        // it uploads carries it (useChatSend waits for the upload to land).
        const placeholderId = chatStore.addUploadingAttachment(file.name);
        const controller = new AbortController();
        pendingChipsRef.current.set(placeholderId, controller);
        const attach = attachUploadedDocument(placeholderId, file.name);
        await withStallTimeout(
          handleFileUpload(file, `Uploaded/${file.name}`, undefined, {
            onDocumentCreated: attach,
            // The same file uploaded again is not a new document, but it is
            // still the file the user attached.
            onExistingDocument: attach,
            signal: controller.signal,
          }),
          UPLOAD_STALL_TIMEOUT_MS
        );
        // Still unresolved: the upload failed (useDocumentUpload has already
        // said why), threw, or stalled past the bound. Either way nothing may
        // be sent as if the file were attached — and a chip left UPLOADING
        // would park the thread's sends behind it forever. A stalled upload is
        // cancelled here, so it cannot go on to create the document the chip
        // now says failed. (Already gone from the map if the panel unmounted
        // under it; the cleanup cancelled and failed it.)
        if (pendingChipsRef.current.delete(placeholderId)) {
          controller.abort();
          chatStore.failUploadingAttachment(placeholderId);
        }
      }

      clearPickerSelection();
    };

    // A queued run resolves the runner when its turn comes, never the one from
    // the render that queued it: the project, the upload callback bound to it
    // and the readiness gate all move while a file waits behind another upload,
    // and a run holding the old ones files into a project the user has left (or
    // refuses a project that has since loaded).
    const uploadFilesSeriallyRef = useRef(uploadFilesSerially);
    uploadFilesSeriallyRef.current = uploadFilesSerially;

    /**
     * One upload at a time, across every caller (the drop path has no
     * `isUploading` guard), so chips resolve in the order files were given.
     * Queue instead of dropping: a file the user dropped mid-upload still
     * uploads. The one exception is an upload past its stall bound — the queue
     * moves on without it, which is safe because it can only resolve its own
     * chip.
     */
    const uploadFiles = (files: FileList | File[]): Promise<void> => {
      // Copy the list HERE, not when the run's turn comes: the picker hands us
      // the live `input.files`, and the run ahead of this one resets the input
      // when it finishes — which empties that very FileList. A file queued
      // behind another then uploads nothing, with no chip and no error.
      const list = Array.from(files);
      const run = uploadChainRef.current.then(() => uploadFilesSeriallyRef.current(list));
      uploadChainRef.current = run.catch(() => undefined);
      return run;
    };

    useImperativeHandle(
      ref,
      () => ({
        openFilePicker: () => inputRef.current?.click(),
        uploadFiles,
        isUploading,
      }),
      // `uploadFiles` resolves the runner through the ref, so the handle no
      // longer has to be rebuilt when the hidden project arrives.
      [isUploading]
    );

    return (
      <>
        {renderTrigger && (
          <button
            type="button"
            disabled={isUploading}
            onClick={() => inputRef.current?.click()}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
          >
            <Paperclip className="h-4 w-4" />
            {isUploading ? "Uploading…" : "Upload"}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          accept={CHATBOT_SUPPORTED_FILE_EXTENSIONS.map((e) => `.${e}`).join(",")}
          onChange={(e) => uploadFiles(e.target.files ?? [])}
        />
      </>
    );
  })
);

ChatbotUploadButton.displayName = "ChatbotUploadButton";
